import { ENVIRONMENT_CONFIGS } from '@choco-admin/utils';
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';

import { useEnvironment } from './EnvironmentContext';

export type PuppyFilterMode = 'normal' | 'puppy' | 'all';

interface PuppyModeContextType {
  /**
   * Whether puppy mode toggle is available (non-production environment)
   */
  isPuppyModeAvailable: boolean;

  /**
   * Current filter mode
   */
  filterMode: PuppyFilterMode;

  /**
   * Filter value for GraphQL queries
   * - undefined: show all users
   * - true: show only puppy users
   * - false: show only normal users
   */
  puppyFilter: boolean | undefined;

  /**
   * Current environment information
   */
  environment: {
    key: string;
    name: string;
    emoji: string;
    isProduction: boolean;
  };

  /**
   * Set the filter mode
   */
  setFilter: (mode: PuppyFilterMode) => void;

  /**
   * Toggle functions for convenience
   */
  toggleToPuppyUsers: () => void;
  toggleToNormalUsers: () => void;
  toggleToAllUsers: () => void;
}

const PuppyModeContext = createContext<PuppyModeContextType | undefined>(
  undefined
);

interface PuppyModeProviderProps {
  children: ReactNode;
}

/**
 * PuppyModeProvider - Global context provider for puppy mode filtering
 *
 * This provider manages the global state for puppy user filtering across the entire application.
 * All queries that use isPuppy filtering will automatically react to changes in this context.
 *
 * Features:
 * - Global state management for filter mode
 * - Automatic query invalidation when filter changes
 * - Production safety (disabled in production environments)
 * - Environment-aware configuration
 */
export const PuppyModeProvider: React.FC<PuppyModeProviderProps> = ({
  children,
}) => {
  const { currentEnvironment } = useEnvironment();
  const [filterMode, setFilterMode] = useState<PuppyFilterMode>('normal');

  const contextValue = useMemo(() => {
    const environmentConfig = ENVIRONMENT_CONFIGS[currentEnvironment];
    const isPuppyModeAvailable = !environmentConfig.isProduction;

    // In production, always filter out puppy users
    const effectiveFilterMode = isPuppyModeAvailable ? filterMode : 'normal';

    // Convert filter mode to GraphQL filter value
    const puppyFilter =
      effectiveFilterMode === 'all'
        ? undefined
        : effectiveFilterMode === 'puppy';

    return {
      isPuppyModeAvailable,
      filterMode: effectiveFilterMode,
      puppyFilter,
      environment: {
        key: currentEnvironment,
        name: environmentConfig.name,
        emoji: environmentConfig.emoji,
        isProduction: environmentConfig.isProduction,
      },
    };
  }, [currentEnvironment, filterMode]);

  const setFilter = useCallback((mode: PuppyFilterMode) => {
    setFilterMode(mode);
  }, []);

  const toggleToPuppyUsers = useCallback(() => {
    setFilterMode('puppy');
  }, []);

  const toggleToNormalUsers = useCallback(() => {
    setFilterMode('normal');
  }, []);

  const toggleToAllUsers = useCallback(() => {
    setFilterMode('all');
  }, []);

  const value: PuppyModeContextType = {
    ...contextValue,
    setFilter,
    toggleToPuppyUsers,
    toggleToNormalUsers,
    toggleToAllUsers,
  };

  return (
    <PuppyModeContext.Provider value={value}>
      {children}
    </PuppyModeContext.Provider>
  );
};

/**
 * Hook to access the puppy mode context
 *
 * This hook provides access to the global puppy mode state and controls.
 * All components using this hook will automatically re-render when the filter mode changes.
 *
 * @returns PuppyModeContextType object with filter state and controls
 * @throws Error if used outside of PuppyModeProvider
 */
export const usePuppyMode = (): PuppyModeContextType => {
  const context = useContext(PuppyModeContext);
  if (context === undefined) {
    throw new Error('usePuppyMode must be used within a PuppyModeProvider');
  }
  return context;
};
